import type { LandingSections } from '@/types/course';

type Instructor = NonNullable<LandingSections['instructor']>;

interface CourseInstructorSectionProps {
  instructor?: Instructor | null;
  lecturerHtml?: string;
  detailIntroduceHtml?: string;
  className?: string;
}

const HTML_PROSE =
  'text-m2-on-surface-variant leading-relaxed [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-2';

const DEFAULT_SECTION_LABEL = 'GIẢNG VIÊN HƯỚNG DẪN';

const CourseInstructorSection = ({
  instructor,
  lecturerHtml,
  detailIntroduceHtml,
  className = '',
}: CourseInstructorSectionProps) => {
  const hasStructured = Boolean(instructor?.name || instructor?.imageUrl);

  if (!hasStructured) {
    return (
      <section id="mentor" className={className}>
        <h2 className="text-2xl font-bold text-m2-on-surface mb-4">Mentor</h2>
        {lecturerHtml ? (
          <div className={HTML_PROSE} dangerouslySetInnerHTML={{ __html: lecturerHtml }} />
        ) : (
          <p className="text-m2-on-surface-variant">Thông tin giảng viên sẽ được cập nhật.</p>
        )}
        {detailIntroduceHtml && (
          <div
            className={`mt-4 ${HTML_PROSE}`}
            dangerouslySetInnerHTML={{ __html: detailIntroduceHtml }}
          />
        )}
      </section>
    );
  }

  const sectionLabel = instructor?.sectionLabel?.trim() || DEFAULT_SECTION_LABEL;
  const stats = (instructor?.stats || []).filter((s) => s.value.trim() || s.label.trim());

  return (
    <section id="mentor" className={`rounded-eh overflow-hidden bg-eh-sage text-eh-charcoal ${className}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative min-h-[280px] lg:min-h-[420px] bg-eh-slate">
          {instructor?.imageUrl ? (
            <img
              src={instructor.imageUrl}
              alt={instructor.name || 'Giảng viên'}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-eh-muted text-sm">
              Chưa có ảnh giảng viên
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-eh-forest-ink/45 via-transparent to-transparent" />
          {instructor?.imageOverlay && (
            <p className="absolute left-4 bottom-6 lg:bottom-10 text-eh-cream text-lg sm:text-xl font-semibold tracking-wide drop-shadow-md max-w-[70%]">
              {instructor.imageOverlay}
            </p>
          )}
        </div>

        <div className="flex flex-col justify-center px-6 py-8 sm:px-10 sm:py-12 bg-eh-sage">
          <p className="text-eh-forest-ink text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase font-mono">
            {sectionLabel}
          </p>
          {instructor?.name && (
            <h2 className="eh-display mt-3 text-3xl sm:text-4xl">
              {instructor.name}
            </h2>
          )}
          {instructor?.bio && (
            <p className="mt-5 text-eh-charcoal leading-relaxed text-sm sm:text-base whitespace-pre-wrap">
              {instructor.bio}
            </p>
          )}
          {stats.length > 0 && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {stats.map((stat) => (
                <div
                  key={`${stat.value}-${stat.label}`}
                  className="border-l-2 border-eh-forest-ink pl-4"
                >
                  <p className="text-2xl sm:text-3xl font-bold text-eh-forest-ink">{stat.value}</p>
                  <p className="mt-1 text-xs tracking-wider uppercase text-eh-muted">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CourseInstructorSection;
